import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import ZoomControlBar from './ZoomControlBar';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-editor-classic/src/classiceditor';

// Essentials
import Essentials from '@ckeditor/ckeditor5-essentials/src/essentials';
import Paragraph from '@ckeditor/ckeditor5-paragraph/src/paragraph';
import Heading from '@ckeditor/ckeditor5-heading/src/heading';

// Basic Styles
import Bold from '@ckeditor/ckeditor5-basic-styles/src/bold';
import Italic from '@ckeditor/ckeditor5-basic-styles/src/italic';
import Underline from '@ckeditor/ckeditor5-basic-styles/src/underline';
import Strikethrough from '@ckeditor/ckeditor5-basic-styles/src/strikethrough';
import Code from '@ckeditor/ckeditor5-basic-styles/src/code';
import Subscript from '@ckeditor/ckeditor5-basic-styles/src/subscript';
import Superscript from '@ckeditor/ckeditor5-basic-styles/src/superscript';

// Font & Alignment
import Font from '@ckeditor/ckeditor5-font/src/font';
import Alignment from '@ckeditor/ckeditor5-alignment/src/alignment';
import Indent from '@ckeditor/ckeditor5-indent/src/indent';
import RemoveFormat from '@ckeditor/ckeditor5-remove-format/src/removeformat';

// Lists
import List from '@ckeditor/ckeditor5-list/src/list';
import TodoList from '@ckeditor/ckeditor5-list/src/todolist';

// Link
import Link from '@ckeditor/ckeditor5-link/src/link';

// Image
import Image from '@ckeditor/ckeditor5-image/src/image';
import ImageUpload from '@ckeditor/ckeditor5-image/src/imageupload';
import ImageToolbar from '@ckeditor/ckeditor5-image/src/imagetoolbar';
import ImageCaption from '@ckeditor/ckeditor5-image/src/imagecaption';
import ImageStyle from '@ckeditor/ckeditor5-image/src/imagestyle';
import ImageResize from '@ckeditor/ckeditor5-image/src/imageresize';
import Base64UploadAdapter from '@ckeditor/ckeditor5-upload/src/adapters/base64uploadadapter';

// Table
import Table from '@ckeditor/ckeditor5-table/src/table';
import TableToolbar from '@ckeditor/ckeditor5-table/src/tabletoolbar';
import TableProperties from '@ckeditor/ckeditor5-table/src/tableproperties';
import TableCellProperties from '@ckeditor/ckeditor5-table/src/tablecellproperties';
import TableCaption from '@ckeditor/ckeditor5-table/src/tablecaption';

// Advanced
import PageBreak from '@ckeditor/ckeditor5-page-break/src/pagebreak';
import FindAndReplace from '@ckeditor/ckeditor5-find-and-replace/src/findandreplace';
import Mention from '@ckeditor/ckeditor5-mention/src/mention';
import PasteFromOffice from '@ckeditor/ckeditor5-paste-from-office/src/pastefromoffice';
import BlockQuote from '@ckeditor/ckeditor5-block-quote/src/blockquote';
import CodeBlock from '@ckeditor/ckeditor5-code-block/src/codeblock';
import HorizontalLine from '@ckeditor/ckeditor5-horizontal-line/src/horizontalline';
import SpecialCharacters from '@ckeditor/ckeditor5-special-characters/src/specialcharacters';
import SpecialCharactersEssentials from '@ckeditor/ckeditor5-special-characters/src/specialcharactersessentials';
import MediaEmbed from '@ckeditor/ckeditor5-media-embed/src/mediaembed';
import SelectAll from '@ckeditor/ckeditor5-select-all/src/selectall';
import ShowBlocks from '@ckeditor/ckeditor5-show-blocks/src/showblocks';
import SourceEditing from '@ckeditor/ckeditor5-source-editing/src/sourceediting';

// Theme
import '@ckeditor/ckeditor5-theme-lark/theme/theme.css';
import './RichTextEditor.css';

// Build editor with all plugins
ClassicEditor.builtinPlugins = [
  Essentials,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Code,
  Subscript,
  Superscript,
  Paragraph,
  Font,
  Alignment,
  Indent,
  List,
  TodoList,
  Link,
  Image,
  ImageUpload,
  ImageToolbar,
  ImageCaption,
  ImageStyle,
  ImageResize,
  Base64UploadAdapter,
  Table,
  TableToolbar,
  TableProperties,
  TableCellProperties,
  TableCaption,
  PageBreak,
  PasteFromOffice, // Word'den yapıştırma desteği - page break'leri korur
  FindAndReplace,
  RemoveFormat,
  Mention,
  Heading,
  BlockQuote,
  CodeBlock,
  HorizontalLine,
  SpecialCharacters,
  SpecialCharactersEssentials,
  MediaEmbed,
  SelectAll,
  ShowBlocks,
  SourceEditing
];

// Font family options (50+ fonts from public/fonts)
const fontFamilyOptions = [
  'default',
  // Office benzeri
  { title: 'Calibri (Body)', model: 'Calibri, Carlito, Arimo, Arial, sans-serif' },
  { title: 'Calibri Light (Headings)', model: 'Calibri Light, Carlito, Arimo, Arial, sans-serif' },
  'Cambria, Caladea, serif',
  'Times New Roman, Tinos, serif',
  'Arial, Arimo, sans-serif',
  'Courier New, Cousine, monospace',
  // Popüler sans-serif
  'Inter, Arial, sans-serif',
  'Roboto, Arial, sans-serif',
  'Open Sans, Arial, sans-serif',
  'Lato, Arial, sans-serif',
  'Montserrat, Arial, sans-serif',
  'Poppins, Arial, sans-serif',
  'Source Sans 3, Arial, sans-serif',
  'Work Sans, Arial, sans-serif',
  'Raleway, Arial, sans-serif',
  'Ubuntu, Arial, sans-serif',
  'Nunito, Arial, sans-serif',
  'Noto Sans, Arial, sans-serif',
  'Barlow, Arial, sans-serif',
  'Quicksand, Arial, sans-serif',
  'Mulish, Arial, sans-serif',
  'Manrope, Arial, sans-serif',
  'DM Sans, Arial, sans-serif',
  'IBM Plex Sans, Arial, sans-serif',
  'PT Sans, Arial, sans-serif',
  'Oswald, Arial, sans-serif',
  'Rubik, Arial, sans-serif',
  'Cairo, Arial, sans-serif',
  // Serif fonts
  'Noto Serif, Georgia, serif',
  'Merriweather, Georgia, serif',
  'Playfair Display, Georgia, serif',
  'Lora, Georgia, serif',
  'PT Serif, Georgia, serif',
  'Crimson Text, Georgia, serif',
  'Libre Baskerville, Georgia, serif',
  'EB Garamond, Georgia, serif',
  'Roboto Slab, Georgia, serif',
  'Bitter, Georgia, serif',
  // Monospace
  'JetBrains Mono, monospace',
  'Fira Code, monospace',
  'Source Code Pro, monospace',
  'Roboto Mono, monospace',
  'Inconsolata, monospace',
  // Display/Script
  'Bebas Neue, Arial, sans-serif',
  'Pacifico, cursive',
  'Dancing Script, cursive',
  'Lobster, cursive',
  'Caveat, cursive',
  'Shadows Into Light, cursive',
  'Comfortaa, cursive',
  'Righteous, cursive'
];

// Default editor configuration
const defaultEditorConfig = {
  toolbar: {
    items: [
      'undo',
      'redo',
      '|',
      'heading',
      '|',
      'fontFamily',
      'fontSize',
      '|',
      'bold',
      'italic',
      'underline',
      'strikethrough',
      'code',
      'subscript',
      'superscript',
      '|',
      'fontColor',
      'fontBackgroundColor',
      '|',
      'alignment',
      '|',
      'bulletedList',
      'numberedList',
      'todoList',
      'outdent',
      'indent',
      '|',
      'link',
      'blockQuote',
      'codeBlock',
      'insertTable',
      'horizontalLine',
      'imageUpload',
      'mediaEmbed',
      'pageBreak',
      '|',
      'specialCharacters',
      '|',
      'selectAll',
      'showBlocks',
      'sourceEditing',
      '|',
      'findAndReplace',
      'removeFormat'
    ],
    shouldNotGroupWhenFull: true // Tüm butonları göster, 3 nokta menüye gizleme
  },
  image: {
    toolbar: [
      'imageStyle:alignLeft',
      'imageStyle:alignCenter',
      'imageStyle:alignRight',
      '|',
      'toggleImageCaption',
      'imageTextAlternative'
    ]
  },
  table: {
    contentToolbar: [
      'tableColumn',
      'tableRow',
      'mergeTableCells',
      '|',
      'tableProperties',
      'tableCellProperties',
      '|',
      'toggleTableCaption'
    ],
    // Tablo varsayılan ayarları - daha fazla renk seçeneği
    tableProperties: {
      borderColors: [
        '#000000', // Siyah
        '#cccccc', // Açık gri
        '#666666', // Koyu gri
        '#ff0000', // Kırmızı
        '#00ff00', // Yeşil
        '#0000ff', // Mavi
        '#ffff00', // Sarı
        '#ff00ff', // Magenta
        '#00ffff'  // Cyan
      ],
      backgroundColors: [
        '#ffffff', // Beyaz
        '#f5f5f5', // Çok açık gri
        '#e0e0e0', // Açık gri
        '#fff9c4', // Açık sarı
        '#e1f5fe', // Açık mavi
        '#f3e5f5', // Açık mor
        '#e8f5e9', // Açık yeşil
        '#fce4ec', // Açık pembe
        '#fff3e0'  // Açık turuncu
      ]
    },
    tableCellProperties: {
      borderColors: [
        '#000000', // Siyah
        '#cccccc', // Açık gri
        '#666666', // Koyu gri
        '#ff0000', // Kırmızı
        '#00ff00', // Yeşil
        '#0000ff', // Mavi
        '#ffff00', // Sarı
        '#ff00ff', // Magenta
        '#00ffff'  // Cyan
      ],
      backgroundColors: [
        '#ffffff', // Beyaz
        '#f5f5f5', // Çok açık gri
        '#e0e0e0', // Açık gri
        '#fff9c4', // Açık sarı
        '#e1f5fe', // Açık mavi
        '#f3e5f5', // Açık mor
        '#e8f5e9', // Açık yeşil
        '#fce4ec', // Açık pembe
        '#fff3e0'  // Açık turuncu
      ]
    }
  },
  fontFamily: {
    options: fontFamilyOptions,
    supportAllValues: true
  },
  fontSize: {
    options: [
      '8pt',
      '9pt',
      '10pt',
      '11pt',
      '12pt',
      '14pt',
      '16pt',
      '18pt',
      '20pt',
      '22pt',
      '24pt',
      '26pt',
      '28pt',
      '36pt',
      '48pt',
      '72pt'
    ],
    supportAllValues: true
  },
  // Word'den kopyala-yapıştır desteği
  // CKEditor otomatik olarak Word formatını temizler ve uyarlar
  // Page breaks, tables, images, formatting korunur
  typing: {
    transformations: {
      include: [
        // Tipografi dönüşümleri
        'quotes',
        'typography'
      ]
    }
  },
  // Heading options
  heading: {
    options: [
      { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
      { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
      { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
      { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
      { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
      { model: 'heading5', view: 'h5', title: 'Heading 5', class: 'ck-heading_heading5' },
      { model: 'heading6', view: 'h6', title: 'Heading 6', class: 'ck-heading_heading6' }
    ]
  },
  // Code block options
  codeBlock: {
    languages: [
      { language: 'plaintext', label: 'Plain text' },
      { language: 'css', label: 'CSS' },
      { language: 'html', label: 'HTML' },
      { language: 'javascript', label: 'JavaScript' },
      { language: 'json', label: 'JSON' },
      { language: 'markdown', label: 'Markdown' },
      { language: 'php', label: 'PHP' },
      { language: 'python', label: 'Python' },
      { language: 'sql', label: 'SQL' },
      { language: 'typescript', label: 'TypeScript' },
      { language: 'xml', label: 'XML' }
    ]
  },
  // Media embed - YouTube, Vimeo, etc.
  mediaEmbed: {
    previewsInData: true
  },
  // Special characters
  specialCharacters: [
    'Latin',
    'Mathematical',
    'Symbols',
    'Arrows',
    'Currency',
    'Punctuation',
    'Text'
  ],
  // Page break configuration - Word'den yapıştırma için
  pageBreak: {
    // Page break'leri görünür yap
    converterPriority: 'high'
  },
  // Paste from Office configuration - Word'den yapıştırma
  // PasteFromOffice plugin'i otomatik olarak Word'den yapıştırıldığında:
  // - Page break'leri korur
  // - Formatting'i temizler ama yapıyı korur
  // - Tables, images, lists korunur
};

/**
 * İçeriği A4 yüksekliğine göre otomatik sayfalara ayıran fonksiyon
 * Her sayfanın üst ve alt boşlukları (25.4mm) korunur
 * 
 * NOT: Bu fonksiyon devre dışı bırakıldı - kullanıcılar manuel Ctrl+Enter ile page break ekleyebilir
 * Otomatik sayfa oluşturma çok karmaşık ve hatalı sonuçlar veriyordu
 */
// eslint-disable-next-line no-unused-vars
const autoPageBreak = async (editor) => {
  // Sonsuz döngüyü önle - eğer zaten çalışıyorsa veya setData çağrısı sırasındaysa, yeni çağrıyı iptal et
  if (editor._isAutoPageBreaking || editor._isSettingData) {
    return;
  }
  editor._isAutoPageBreaking = true;
  
  try {
    // Önce mevcut page break'leri kontrol et
    const model = editor.model;
    const root = model.document.getRoot();
    const range = model.createRangeIn(root);
    const walker = range.getWalker();
    let existingPageBreaks = 0;
    for (const value of walker) {
      if (value.item.is('element', 'pageBreak')) {
        existingPageBreaks++;
      }
    }
    
    // Eğer zaten page break'ler varsa, yeni ekleme (sonsuz döngüyü önlemek için)
    if (existingPageBreaks > 0) {
      editor._isAutoPageBreaking = false;
      return;
    }
    
    const content = editor.getData();
    let updatedContent = content;
    let hasChanges = false;
    
    // İçerik boşsa veya çok kısaysa, page break eklemeye gerek yok
    if (!content || content.trim().length < 100) {
      editor._isAutoPageBreaking = false;
      return;
    }
    
    // 1. Önce mevcut page break'leri düzelt
    const wordPageBreakPatterns = [
      { pattern: /<div[^>]*style[^>]*page-break-before:\s*always[^>]*>/gi, replacement: '<div class="page-break"></div>' },
      { pattern: /<p[^>]*class[^>]*MsoPageBreak[^>]*>.*?<\/p>/gi, replacement: '<div class="page-break"></div>' },
      { pattern: /<p[^>]*style[^>]*page-break-before:\s*always[^>]*>.*?<\/p>/gi, replacement: '<div class="page-break"></div>' },
      { pattern: /<p[^>]*style[^>]*mso-page-break-before:\s*always[^>]*>.*?<\/p>/gi, replacement: '<div class="page-break"></div>' },
      { pattern: /<div[^>]*style[^>]*page-break-after:\s*always[^>]*>/gi, replacement: '<div class="page-break"></div>' },
      { pattern: /<p[^>]*style[^>]*page-break-after:\s*always[^>]*>.*?<\/p>/gi, replacement: '<div class="page-break"></div>' }
    ];
    
    wordPageBreakPatterns.forEach(({ pattern, replacement }) => {
      if (pattern.test(updatedContent)) {
        updatedContent = updatedContent.replace(pattern, replacement);
        hasChanges = true;
      }
    });
    
    // 2. İçeriği A4 yüksekliğine göre otomatik sayfalara ayır
    const contentArea = editor.ui.getEditableElement();
    if (!contentArea) {
      if (hasChanges) {
        editor.setData(updatedContent);
      }
      return;
    }
    
    // A4 sayfa boyutları: 210mm x 297mm
    // Word'de 12 sayfa, bizde 14 sayfa olması, bizim sayfa yüksekliğimizin Word'den %16.6 daha küçük olduğunu gösteriyor
    // 14/12 = 1.166, yani Word'ün sayfa yüksekliği bizimkinden %16.6 daha büyük
    // Word'e daha yakın olması için margin'leri azaltıyoruz
    // Eski: 25.4mm margin -> 246.2mm sayfa yüksekliği
    // Yeni: 15mm margin -> 267mm sayfa yüksekliği (%8.4 artış)
    // Bu, Word'ün sayfa yüksekliğine daha yakın olacak
    const A4_PAGE_WIDTH_MM = 210;
    const A4_PAGE_HEIGHT_MM = 297;
    const A4_MARGIN_MM = 15; // Word'e daha yakın margin (varsayılan 25.4mm yerine)
    const A4_CONTENT_WIDTH_MM = A4_PAGE_WIDTH_MM - (A4_MARGIN_MM * 2); // 180mm
    const A4_CONTENT_HEIGHT_MM = A4_PAGE_HEIGHT_MM - (A4_MARGIN_MM * 2); // 267mm (Word'e daha yakın)
    
    // Geçici container oluştur - A4 boyutlarında
    // ÖNEMLİ: height: auto olmalı ki içerik tam render edilsin
    const tempContainer = document.createElement('div');
    tempContainer.style.cssText = `
      position: absolute;
      visibility: hidden;
      width: ${A4_CONTENT_WIDTH_MM}mm;
      height: auto !important;
      min-height: auto !important;
      max-height: none !important;
      padding: ${A4_MARGIN_MM}mm;
      font-size: 11pt;
      line-height: 1.35;
      font-family: Calibri, Arial, sans-serif;
      box-sizing: border-box;
      top: -9999px;
      left: -9999px;
      overflow: visible !important;
    `;
    
    // CKEditor stilini kopyala - gerçek editör stillerini kullan
    const computedStyle = window.getComputedStyle(contentArea);
    tempContainer.style.fontSize = computedStyle.fontSize || '11pt';
    tempContainer.style.lineHeight = computedStyle.lineHeight || '1.35';
    tempContainer.style.fontFamily = computedStyle.fontFamily || 'Calibri, Arial, sans-serif';
    tempContainer.style.paddingTop = computedStyle.paddingTop || `${A4_MARGIN_MM}mm`;
    tempContainer.style.paddingBottom = computedStyle.paddingBottom || `${A4_MARGIN_MM}mm`;
    tempContainer.style.paddingLeft = computedStyle.paddingLeft || `${A4_MARGIN_MM}mm`;
    tempContainer.style.paddingRight = computedStyle.paddingRight || `${A4_MARGIN_MM}mm`;
    
    // ÖNEMLİ: Word'den yapıştırılan içerikteki inline style'ları koru
    // Her element'in kendi font-size ve font-family bilgilerini korumak için
    // tempContainer'a yapıştırmadan önce, içerikteki inline style'ları kontrol et
    tempContainer.innerHTML = updatedContent;
    
    document.body.appendChild(tempContainer);
    
    // Elementlerin render edilmesini bekle - önemli!
    // Force reflow için container'ı bir kez oku
    void tempContainer.offsetHeight;
    
    // Bir frame bekle ki tüm içerik render edilsin
    await new Promise(resolve => requestAnimationFrame(resolve));
    
    // mm'yi px'e çevir: 1mm ≈ 3.779527559px (96 DPI'de)
    const A4_CONTENT_HEIGHT_PX = A4_CONTENT_HEIGHT_MM * 3.779527559;
    
    // Block elementleri bul (page-break hariç)
    const blockElements = Array.from(tempContainer.querySelectorAll('p, div:not(.page-break), h1, h2, h3, h4, h5, h6, img, table, figure, ul, ol, blockquote, pre'));
    
    // Eğer block element yoksa, sadece page break düzeltmelerini uygula
    if (blockElements.length === 0) {
      document.body.removeChild(tempContainer);
      editor._isAutoPageBreaking = false;
      if (hasChanges) {
        editor.setData(updatedContent);
      }
      return;
    }
    
    // ÖNEMLİ: scrollHeight'a güvenmeyelim, her zaman text-based calculation yapalım
    // Çünkü hidden container'da scrollHeight doğru çalışmayabilir
    
    // Tüm text içeriğini topla
    const allText = blockElements.map(el => el.textContent || '').join(' ').trim();
    
    let containerTotalHeight = 0;
    
    if (allText.length > 0) {
      // Font size ve line height al (ilk element'ten veya container'dan)
      const firstElementStyle = blockElements[0] ? window.getComputedStyle(blockElements[0]) : null;
      const containerStyle = window.getComputedStyle(tempContainer);
      
      const fontSize = firstElementStyle 
        ? parseFloat(firstElementStyle.fontSize) || 14.67 
        : parseFloat(containerStyle.fontSize) || 14.67; // 11pt = 14.67px
      const lineHeight = firstElementStyle
        ? parseFloat(firstElementStyle.lineHeight) || fontSize * 1.35
        : parseFloat(containerStyle.lineHeight) || fontSize * 1.35;
      
      // Container genişliğine göre satır sayısını hesapla
      const containerWidth = A4_CONTENT_WIDTH_MM * 3.779527559; // px (yaklaşık 601px)
      
      // Canvas kullanarak gerçek karakter genişliğini ölç
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      const fontFamily = firstElementStyle 
        ? firstElementStyle.fontFamily || containerStyle.fontFamily || 'Arial'
        : containerStyle.fontFamily || 'Arial';
      context.font = `${fontSize}px ${fontFamily}`;
      const avgCharWidth = context.measureText('M').width; // 'M' karakteri genişliği
      
      // Satır başına karakter sayısı
      // ÖNEMLİ: Padding'ler zaten container genişliğinin içinde, bu yüzden çıkarmamalıyız
      // Container genişliği zaten içerik genişliği (padding'ler dahil değil)
      const availableWidth = containerWidth; // Padding'leri çıkarmıyoruz, çünkü zaten içerik genişliği bu
      const charsPerLine = Math.floor(availableWidth / avgCharWidth) || 80;
      
      // Toplam satır sayısı
      const totalLines = Math.ceil(allText.length / charsPerLine) || 1;
      
      // Container yüksekliği = satır sayısı * line height + padding'ler
      const paddingTop = parseFloat(containerStyle.paddingTop) || 0;
      const paddingBottom = parseFloat(containerStyle.paddingBottom) || 0;
      containerTotalHeight = (totalLines * lineHeight) + paddingTop + paddingBottom;
      
      // scrollHeight ile karşılaştır
      const scrollHeight = tempContainer.scrollHeight;
      
      // Eğer scrollHeight çok daha büyükse (örneğin 1.5x), onu kullan (belki gerçekten daha doğru)
      if (scrollHeight > containerTotalHeight * 1.5) {
        containerTotalHeight = scrollHeight;
      }
    } else {
      // Text yoksa, element yüksekliklerini topla
      blockElements.forEach((element) => {
        const rect = element.getBoundingClientRect();
        const style = window.getComputedStyle(element);
        const marginTop = parseFloat(style.marginTop) || 0;
        const marginBottom = parseFloat(style.marginBottom) || 0;
        const elementHeight = rect.height || element.scrollHeight || element.offsetHeight || 0;
        containerTotalHeight += elementHeight + marginTop + marginBottom;
      });
      // Padding'leri de ekle
      const containerStyle = window.getComputedStyle(tempContainer);
      const paddingTop = parseFloat(containerStyle.paddingTop) || 0;
      const paddingBottom = parseFloat(containerStyle.paddingBottom) || 0;
      containerTotalHeight += paddingTop + paddingBottom;
    }
    
    // Eğer toplam yükseklik A4 limitini aşmıyorsa, page break gerekmez
    if (containerTotalHeight <= A4_CONTENT_HEIGHT_PX) {
      document.body.removeChild(tempContainer);
      editor._isAutoPageBreaking = false;
      return;
    }
    
    // Toplam yükseklik A4'ü aşıyorsa, her element için yükseklik hesapla
    let currentPageHeight = 0;
    const pageBreakPositions = [];
    
    // Her element için kümülatif yükseklik hesapla
    blockElements.forEach((element, index) => {
      // Page break varsa, sayfa sıfırla
      if (element.classList && element.classList.contains('page-break')) {
        currentPageHeight = 0;
        return;
      }
      
      // Element tipini kontrol et - resim, tablo, figure gibi bölünemez elementler
      const tagName = element.tagName.toLowerCase();
      const isNonBreakable = tagName === 'img' || tagName === 'table' || tagName === 'figure' || 
                             (tagName === 'div' && element.querySelector('img, table, figure'));
      
      // Element yüksekliğini hesapla - daha doğru yöntem
      const elementStyle = window.getComputedStyle(element);
      const marginTop = parseFloat(elementStyle.marginTop) || 0;
      const marginBottom = parseFloat(elementStyle.marginBottom) || 0;
      const paddingTop = parseFloat(elementStyle.paddingTop) || 0;
      const paddingBottom = parseFloat(elementStyle.paddingBottom) || 0;
      
      // scrollHeight kullan - içeriğin gerçek yüksekliği
      let elementHeight = element.scrollHeight;
      
      // Boş paragraph'lar için özel kontrol (enter enter ile oluşturulan boş satırlar)
      const textContent = element.textContent || '';
      const isBlankParagraph = textContent.trim().length === 0 && tagName === 'p';
      
      if (isBlankParagraph) {
        // Boş paragraph için line-height kullan
        const lineHeight = parseFloat(elementStyle.lineHeight) || parseFloat(elementStyle.fontSize) * 1.35 || 20;
        elementHeight = Math.max(elementHeight, lineHeight);
      }
      
      // Resim, tablo gibi elementler için özel yükseklik hesaplama
      if (isNonBreakable) {
        // getBoundingClientRect() ile gerçek yüksekliği al
        const rect = element.getBoundingClientRect();
        if (rect.height > 0) {
          elementHeight = rect.height;
        } else if (element.offsetHeight > 0) {
          elementHeight = element.offsetHeight;
        }
        // Resim için naturalHeight kontrolü
        if (tagName === 'img' && element.naturalHeight) {
          const naturalHeight = element.naturalHeight;
          const naturalWidth = element.naturalWidth;
          const containerWidth = A4_CONTENT_WIDTH_MM * 3.779527559;
          if (naturalWidth > 0) {
            const scaledHeight = (naturalHeight / naturalWidth) * Math.min(containerWidth, element.width || containerWidth);
            if (scaledHeight > elementHeight) {
              elementHeight = scaledHeight;
            }
          }
        }
      } else {
        // Text elementleri için mevcut mantık
        // Eğer scrollHeight çok küçükse veya 0 ise, text içeriğine göre hesapla
        if (elementHeight < 50 && element.textContent) {
          const text = element.textContent.trim();
          if (text.length > 0) {
            // Font size ve line height al - önce element'in kendi style'ından, sonra parent'tan, sonra container'dan
            let fontSize = parseFloat(elementStyle.fontSize);
            let fontFamily = elementStyle.fontFamily || 'Arial';
            
            // Eğer fontSize yoksa veya 0 ise, parent element'ten al
            if (!fontSize || fontSize === 0) {
              const parentElement = element.parentElement;
              if (parentElement) {
                const parentStyle = window.getComputedStyle(parentElement);
                fontSize = parseFloat(parentStyle.fontSize);
                fontFamily = parentStyle.fontFamily || fontFamily;
              }
            }
            
            // Hala yoksa, container'dan al
            if (!fontSize || fontSize === 0) {
              fontSize = parseFloat(computedStyle.fontSize) || 14.67; // 11pt = 14.67px
              fontFamily = computedStyle.fontFamily || 'Calibri, Arial, sans-serif';
            }
            
            // Line height al
            let lineHeight = parseFloat(elementStyle.lineHeight);
            if (!lineHeight || lineHeight === 0) {
              lineHeight = fontSize * 1.35; // Varsayılan line-height
            } else if (lineHeight < 10) {
              // Eğer line-height çok küçükse (örneğin 1.35 gibi bir sayı), fontSize ile çarp
              lineHeight = fontSize * lineHeight;
            }
            
            // Container genişliğine göre satır sayısını hesapla
            const containerWidth = A4_CONTENT_WIDTH_MM * 3.779527559; // px (yaklaşık 601px)
            
            // Canvas kullanarak gerçek karakter genişliğini ölç (daha doğru)
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            context.font = `${fontSize}px ${fontFamily}`;
            const avgCharWidth = context.measureText('M').width; // 'M' karakteri genişliği
            
            // Satır başına karakter sayısı
            const availableWidth = containerWidth;
            const charsPerLine = Math.floor(availableWidth / avgCharWidth) || 80;
            
            // Satır sayısını hesapla
            const lines = Math.ceil(text.length / charsPerLine) || 1;
            
            // Element yüksekliği = satır sayısı * line height
            elementHeight = lines * lineHeight;
          }
        }
        
        // getBoundingClientRect() ile de kontrol et
        const rect = element.getBoundingClientRect();
        if (rect.height > elementHeight) {
          elementHeight = rect.height;
        }
        
        // offsetHeight ile de kontrol et
        if (element.offsetHeight > elementHeight) {
          elementHeight = element.offsetHeight;
        }
      }
      
      // Elementler arası boşluk: önceki element'in margin-bottom'u + bu element'in margin-top'u
      // İlk element değilse, önceki element'in margin-bottom'unu da ekle
      let spacingBefore = 0;
      if (index > 0) {
        const prevElement = blockElements[index - 1];
        if (prevElement && !prevElement.classList?.contains('page-break')) {
          const prevStyle = window.getComputedStyle(prevElement);
          const prevMarginBottom = parseFloat(prevStyle.marginBottom) || 0;
          // CSS margin collapse: iki margin'den büyük olanı al
          spacingBefore = Math.max(marginTop, prevMarginBottom);
        } else {
          spacingBefore = marginTop;
        }
      } else {
        spacingBefore = marginTop;
      }
      
      // Toplam yükseklik: element + spacing + margin + padding
      const totalHeight = elementHeight + spacingBefore + marginBottom + paddingTop + paddingBottom;
      
      // Bölünemez elementler (resim, tablo) için özel mantık
      if (isNonBreakable) {
        // Element mevcut sayfaya sığıyor mu? (spacingBefore dahil)
        const newPageHeight = currentPageHeight + totalHeight;
        if (newPageHeight > A4_CONTENT_HEIGHT_PX && currentPageHeight > 0) {
          // Sayfa doldu, element önüne page break ekle
          pageBreakPositions.push({ index, before: true });
          // Yeni sayfada başla, spacingBefore'ı dahil etme çünkü page break'ten sonra başlıyor
          currentPageHeight = elementHeight + marginBottom + paddingTop + paddingBottom;
        } else {
          // Sayfaya ekle
          currentPageHeight = newPageHeight;
        }
      } else {
        // Text elementleri için mevcut mantık
        // Eğer element tek başına sayfa yüksekliğini aşıyorsa, içine page break'ler ekle
        if (totalHeight > A4_CONTENT_HEIGHT_PX) {
          // Element çok uzun, içine page break'ler ekle
          
          // İlk sayfa için mevcut sayfa yüksekliğini kullan
          let remainingHeight = totalHeight;
          let elementCurrentPage = currentPageHeight;
          
          // İlk parça mevcut sayfaya sığıyor mu?
          if (elementCurrentPage > 0) {
            const firstPageSpace = A4_CONTENT_HEIGHT_PX - elementCurrentPage;
            if (firstPageSpace < remainingHeight) {
              // İlk parça mevcut sayfaya sığıyor, sonraki parçalara page break ekle
              remainingHeight -= firstPageSpace;
              elementCurrentPage = 0;
            }
          }
          
          // Kalan yükseklik için page break'ler ekle (her A4_CONTENT_HEIGHT_PX'te bir)
          let pageBreakCount = 0;
          while (remainingHeight > A4_CONTENT_HEIGHT_PX) {
            pageBreakCount++;
            // Element içinde karakter pozisyonunu hesapla
            const textLength = (element.textContent || '').length;
            const charsPerPage = Math.floor((A4_CONTENT_HEIGHT_PX / totalHeight) * textLength);
            const charPosition = charsPerPage * pageBreakCount;
            
            if (charPosition < textLength) {
              pageBreakPositions.push({ 
                index, 
                before: false, 
                inside: true, 
                charPosition: charPosition
              });
              remainingHeight -= A4_CONTENT_HEIGHT_PX;
            } else {
              break; // Son karaktere ulaştık
            }
          }
          
          // Son sayfa için yüksekliği güncelle
          currentPageHeight = remainingHeight;
        } else {
          // Element mevcut sayfaya eklenebilir mi kontrol et (spacingBefore dahil)
          const newPageHeight = currentPageHeight + totalHeight;
          if (newPageHeight > A4_CONTENT_HEIGHT_PX && currentPageHeight > 0) {
            // Sayfa doldu, yeni sayfa başlat
            pageBreakPositions.push({ index, before: true });
            // Yeni sayfada başla, spacingBefore'ı dahil etme çünkü page break'ten sonra başlıyor
            currentPageHeight = elementHeight + marginBottom + paddingTop + paddingBottom;
          } else {
            // Sayfaya ekle
            currentPageHeight = newPageHeight;
          }
        }
      }
    });
    
    // Page break'leri HTML string'e ekle (ters sırada, böylece index'ler bozulmaz)
    // CKEditor PageBreak eklentisi için format: <div class="page-break"></div>
    const pageBreakHtml = '<div class="page-break"></div>';
    
    // HTML string'i parse et ve page break'leri ekle
    // Önce tüm block elementlerinin HTML'deki pozisyonlarını bul
    const htmlPositions = [];
    blockElements.forEach((element) => {
      const elementHtml = element.outerHTML;
      const index = updatedContent.indexOf(elementHtml);
      if (index !== -1) {
        htmlPositions.push({
          element,
          html: elementHtml,
          startIndex: index,
          endIndex: index + elementHtml.length
        });
      }
    });
    
    // Önce inside olanları grupla (aynı element için)
    const insideBreaksByElement = {};
    pageBreakPositions.filter(p => p.inside).forEach(({ index, charPosition }) => {
      if (!insideBreaksByElement[index]) {
        insideBreaksByElement[index] = [];
      }
      insideBreaksByElement[index].push(charPosition);
    });
    
    // Her element için, tüm page break'leri birlikte işle (ters sırada)
    Object.keys(insideBreaksByElement).reverse().forEach(elementIndexStr => {
      const elementIndex = parseInt(elementIndexStr);
      if (elementIndex < htmlPositions.length) {
        const pos = htmlPositions[elementIndex];
        const element = pos.element;
        const elementText = element.textContent || '';
        const elementHtml = pos.html;
        const tagName = element.tagName.toLowerCase();
        
        // Bu element için tüm page break pozisyonlarını sırala (büyükten küçüğe)
        const breakPositions = insideBreaksByElement[elementIndex].sort((a, b) => b - a);
        
        // CRITICAL: Orijinal element'in tüm attribute'lerini koru (style, class, id, vb.)
        // Element'in opening tag'ini parse et ve tüm attribute'leri al
        const openingTagMatch = elementHtml.match(new RegExp(`<${tagName}[^>]*>`, 'i'));
        const openingTag = openingTagMatch ? openingTagMatch[0] : `<${tagName}>`;
        
        // CRITICAL: HTML içeriğini koru (nested element'ler, inline style'lar, vb.)
        // textContent yerine innerHTML kullan ki nested element'ler kaybolmasın
        const elementInnerHtml = element.innerHTML || '';
        
        // Element'i parçalara böl - HTML içeriğini koruyarak
        // Text pozisyonunu HTML içeriğindeki gerçek pozisyona çevir
        let currentHtml = elementInnerHtml;
        let resultHtml = '';
        let lastPosition = elementText.length;
        
        breakPositions.forEach((charPosition) => {
          if (charPosition > 0 && charPosition < lastPosition) {
            // Text pozisyonunu HTML içeriğindeki gerçek pozisyona çevir
            // HTML tag'lerini atlayarak karakter sayısını hesapla
            let htmlPosition = 0;
            let textCount = 0;
            let inTag = false;
            
            for (let i = 0; i < elementInnerHtml.length && textCount < charPosition; i++) {
              const char = elementInnerHtml[i];
              if (char === '<') {
                inTag = true;
              } else if (char === '>') {
                inTag = false;
              } else if (!inTag) {
                textCount++;
                if (textCount === charPosition) {
                  htmlPosition = i + 1;
                  break;
                }
              }
            }
            
            // HTML içeriğini böl
            const afterHtml = currentHtml.substring(htmlPosition);
            const beforeHtml = currentHtml.substring(0, htmlPosition);
            
            // Yeni element oluştur (afterHtml ile) - TÜM ATTRIBUTE'LERİ VE NESTED ELEMENT'LERİ KORU
            if (afterHtml.trim()) {
              resultHtml = pageBreakHtml + openingTag + afterHtml + `</${tagName}>` + resultHtml;
            } else {
              resultHtml = pageBreakHtml + resultHtml;
            }
            
            currentHtml = beforeHtml;
            lastPosition = charPosition;
          }
        });
        
        // İlk element (kalan HTML ile) - TÜM ATTRIBUTE'LERİ VE NESTED ELEMENT'LERİ KORU
        const textStartIndex = elementHtml.indexOf('>') + 1;
        const textEndIndex = elementHtml.lastIndexOf('<');
        const firstElementHtml = elementHtml.substring(0, textStartIndex) + 
                                 currentHtml + 
                                 elementHtml.substring(textEndIndex);
        
        // Tüm HTML'i birleştir
        const newElementHtml = firstElementHtml + resultHtml;
        
        // HTML'de değiştir
        updatedContent = updatedContent.slice(0, pos.startIndex) + 
                        newElementHtml + 
                        updatedContent.slice(pos.endIndex);
        
        // Sonraki pozisyonları güncelle
        const lengthDiff = newElementHtml.length - elementHtml.length;
        htmlPositions.forEach(p => {
          if (p.startIndex > pos.endIndex) {
            p.startIndex += lengthDiff;
            p.endIndex += lengthDiff;
          } else if (p.startIndex === pos.startIndex && p.endIndex === pos.endIndex) {
            p.endIndex += lengthDiff;
          }
        });
        
        hasChanges = true;
      }
    });
    
    // Diğer page break'leri ekle (before/after) - ÖNEMLİ: index'e göre sırala
    const otherBreaks = pageBreakPositions.filter(p => !p.inside).sort((a, b) => {
      // before: true olanları önce, sonra index'e göre
      if (a.before !== b.before) {
        return a.before ? -1 : 1;
      }
      return a.index - b.index;
    });
    
    // Ters sırada işle ki index'ler bozulmasın
    otherBreaks.reverse().forEach(({ index, before }) => {
      if (index < htmlPositions.length) {
        const pos = htmlPositions[index];
        if (before) {
          // Element'ten önce ekle
          updatedContent = updatedContent.slice(0, pos.startIndex) + pageBreakHtml + updatedContent.slice(pos.startIndex);
          // Sonraki pozisyonları güncelle
          htmlPositions.forEach(p => {
            if (p.startIndex >= pos.startIndex) {
              p.startIndex += pageBreakHtml.length;
              p.endIndex += pageBreakHtml.length;
            }
          });
        } else {
          // Element'ten sonra ekle
          updatedContent = updatedContent.slice(0, pos.endIndex) + pageBreakHtml + updatedContent.slice(pos.endIndex);
          // Sonraki pozisyonları güncelle
          htmlPositions.forEach(p => {
            if (p.startIndex > pos.endIndex) {
              p.startIndex += pageBreakHtml.length;
              p.endIndex += pageBreakHtml.length;
            }
          });
        }
        hasChanges = true;
      }
    });
    
    document.body.removeChild(tempContainer);
    
    if (hasChanges && pageBreakPositions.length > 0) {
      // HTML string'de page break'lerin gerçekten eklendiğini kontrol et
      const pageBreakCountInHtml = (updatedContent.match(/<div class="page-break"><\/div>/g) || []).length;
      
      if (pageBreakCountInHtml === 0) {
        console.warn('No page breaks found in HTML string! HTML manipulation may have failed.');
        editor._isAutoPageBreaking = false;
        return;
      }
      
      // HTML string'deki page break pozisyonlarını ve hangi element'in önünde/sonrasında olduğunu kaydet
      const pageBreakPositionsInHtml = [];
      let searchIndex = 0;
      while (true) {
        const index = updatedContent.indexOf('<div class="page-break"></div>', searchIndex);
        if (index === -1) break;
        
        // Bu page break'in hangi element'in önünde/sonrasında olduğunu bul
        let elementIndex = -1;
        let isBefore = false;
        
        // htmlPositions'ı kontrol et (güncel pozisyonlar)
        for (let i = 0; i < htmlPositions.length; i++) {
          const pos = htmlPositions[i];
          // Page break element'in önündeyse
          if (index < pos.startIndex) {
            elementIndex = i;
            isBefore = true;
            break;
          }
          // Page break element'in sonrasındaysa
          if (index >= pos.startIndex && index <= pos.endIndex + 50) {
            elementIndex = i;
            isBefore = false;
            break;
          }
        }
        
        // Eğer bulunamadıysa, en yakın element'i bul
        if (elementIndex === -1) {
          let closestIndex = 0;
          let minDistance = Math.abs(index - htmlPositions[0].startIndex);
          for (let i = 1; i < htmlPositions.length; i++) {
            const distance = Math.abs(index - htmlPositions[i].startIndex);
            if (distance < minDistance) {
              minDistance = distance;
              closestIndex = i;
            }
          }
          elementIndex = closestIndex;
          isBefore = index < htmlPositions[closestIndex].startIndex;
        }
        
        pageBreakPositionsInHtml.push({
          htmlPosition: index,
          elementIndex: elementIndex,
          isBefore: isBefore,
          elementTag: elementIndex >= 0 ? htmlPositions[elementIndex].element.tagName.toLowerCase() : 'unknown'
        });
        
        searchIndex = index + 1;
      }
      
      // HTML string'den page break'leri kaldır (setData için)
      const contentWithoutPageBreaks = updatedContent.replace(/<div class="page-break"><\/div>/g, '');
      
      // setData çağrısı sırasında onChange'i ignore et
      editor._isSettingData = true;
      
      // setData ile içeriği set et
      editor.setData(contentWithoutPageBreaks);
      
      // setData'dan sonra, page break'leri model'e ekle
      setTimeout(() => {
        // model.change içindeki değişikliklerin onChange'i tetiklemesini engelle
        editor._isAutoPageBreaking = true;
        
        editor.model.change((writer) => {
          const root = editor.model.document.getRoot();
          
          // Model'deki tüm paragraph'ları topla
          
          // HTML string'deki page break pozisyonlarını, model'deki pozisyonlara çevir
          // Basit yaklaşım: Her page break için, en yakın paragraph'ı bul ve önüne ekle
          const rootChildren = Array.from(root.getChildren());
          
          // HTML string'deki page break pozisyonlarını, model'deki pozisyonlara çevir
          // Önce tüm paragraph'ları ve karakter sayılarını topla
          const paragraphMap = [];
          let totalModelChars = 0;
          for (let i = 0; i < rootChildren.length; i++) {
            const item = rootChildren[i];
            if (item.is('element', 'paragraph')) {
              const paragraphText = Array.from(item.getChildren())
                .filter(node => node.is('$text'))
                .map(node => node.data)
                .join('');
              paragraphMap.push({
                item,
                index: i,
                start: totalModelChars,
                end: totalModelChars + paragraphText.length,
                text: paragraphText
              });
              totalModelChars += paragraphText.length;
            }
          }
          
          // Model'deki tüm elementleri topla (paragraph, heading, figure, table, vs.)
          const modelElements = [];
          for (let i = 0; i < rootChildren.length; i++) {
            const item = rootChildren[i];
            if (item.is('element')) {
              modelElements.push({
                item,
                index: i,
                type: item.name
              });
            }
          }
          
          pageBreakPositionsInHtml.forEach((breakInfo, breakIndex) => {
            const { htmlPosition, elementIndex, isBefore, elementTag } = breakInfo;
            
            // Text elementleri (p, h1, h2, etc.) için eski mantığı kullan (karakter pozisyonuna göre)
            // Bölünemez elementler (figure, table) için yeni mantığı kullan (element index'ine göre)
            const isTextElement = elementTag === 'p' || elementTag === 'h1' || elementTag === 'h2' || 
                                  elementTag === 'h3' || elementTag === 'h4' || elementTag === 'h5' || 
                                  elementTag === 'h6' || elementTag === 'div' || elementTag === 'ul' || 
                                  elementTag === 'ol' || elementTag === 'blockquote';
            
            let foundPosition = null;
            
            if (isTextElement) {
              // Text elementleri için: HTML pozisyonunu karakter sayısına çevir ve paragraph map'i kullan
              // HTML string'deki page break pozisyonundan önceki text içeriğini say
              const textBeforeBreak = updatedContent.substring(0, htmlPosition);
              // HTML tag'lerini ve attribute'lerini kaldırarak sadece text içeriğini al
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = textBeforeBreak;
              const textContent = tempDiv.textContent || tempDiv.innerText || '';
              const htmlCharCount = textContent.length;
              
              // Paragraph map'te en yakın paragraph'ı bul
              for (let i = 0; i < paragraphMap.length; i++) {
                const para = paragraphMap[i];
                
                // Eğer page break pozisyonu bu paragraph içindeyse veya hemen sonrasındaysa
                if (htmlCharCount >= para.start && htmlCharCount <= para.end + 50) {
                  foundPosition = {
                    item: para.item,
                    index: para.index,
                    insertAfter: !isBefore,
                    reason: `text element: paragraph ${i}, start=${para.start}, end=${para.end}, htmlCharCount=${htmlCharCount}`
                  };
                  break;
                }
              }
              
              // Eğer hala bulunamadıysa, en yakın paragraph'ı bul
              if (!foundPosition && paragraphMap.length > 0) {
                let closestPara = paragraphMap[0];
                let minDistance = Math.abs(htmlCharCount - closestPara.start);
                
                for (let i = 1; i < paragraphMap.length; i++) {
                  const para = paragraphMap[i];
                  const distance = Math.abs(htmlCharCount - para.start);
                  if (distance < minDistance) {
                    minDistance = distance;
                    closestPara = para;
                  }
                }
                
                foundPosition = {
                  item: closestPara.item,
                  index: closestPara.index,
                  insertAfter: !isBefore,
                  reason: `text element: closest paragraph (distance: ${minDistance})`
                };
              }
            } else {
              // Bölünemez elementler (figure, table) için: element index'ine göre eşleştirme
              if (elementIndex >= 0 && elementIndex < blockElements.length) {
                const htmlElement = blockElements[elementIndex];
                const htmlTagName = htmlElement.tagName.toLowerCase();
                
                // HTML'deki bu tip element'in kaçıncı olduğunu bul (aynı tip elementler arasında)
                let htmlTypeCount = 0;
                for (let i = 0; i <= elementIndex; i++) {
                  if (blockElements[i].tagName.toLowerCase() === htmlTagName) {
                  htmlTypeCount++;
                }
              }
              
              // Model'de aynı tip ve sıradaki element'i bul
                let modelElementIndex = -1;
                let modelTypeCount = 0;
                
                for (let i = 0; i < modelElements.length; i++) {
                  const modelEl = modelElements[i];
                  let matches = false;
                  
                  // Tag tipine göre eşleştirme
                  if (htmlTagName === 'figure' && modelEl.type === 'figure') {
                    matches = true;
                  } else if (htmlTagName === 'table' && modelEl.type === 'table') {
                    matches = true;
                  }
                  
                  if (matches) {
                    modelTypeCount++;
                    if (modelTypeCount === htmlTypeCount) {
                      modelElementIndex = i;
                      break;
                    }
                  }
                }
                
              // Eğer bulunamadıysa, index'e göre direkt eşleştirme yap (fallback)
              if (modelElementIndex === -1 && elementIndex < modelElements.length) {
                modelElementIndex = elementIndex;
              }
              
              if (modelElementIndex >= 0 && modelElementIndex < modelElements.length) {
                const modelEl = modelElements[modelElementIndex];
                foundPosition = {
                  item: modelEl.item,
                  index: modelEl.index,
                  insertAfter: !isBefore,
                  reason: `non-breakable element: matched HTML element ${elementIndex} (${htmlTagName}, ${htmlTypeCount}. of type) to model element ${modelElementIndex} (${modelEl.type})`
                };
              }
              }
            }
            
            // Fallback: Eğer eşleştirme başarısız olursa, paragraph map'i kullan
            if (!foundPosition && paragraphMap.length > 0) {
              // İlk veya son paragraph'ı kullan
              if (isBefore) {
                foundPosition = {
                  item: paragraphMap[0].item,
                  index: paragraphMap[0].index,
                  insertAfter: false,
                  reason: 'fallback: before first paragraph'
                };
              } else {
                foundPosition = {
                  item: paragraphMap[paragraphMap.length - 1].item,
                  index: paragraphMap[paragraphMap.length - 1].index,
                  insertAfter: true,
                  reason: 'fallback: after last paragraph'
                };
              }
            }
            
            if (foundPosition) {
              try {
                const pageBreak = writer.createElement('pageBreak');
                if (foundPosition.insertAfter) {
                  writer.insert(pageBreak, writer.createPositionAfter(foundPosition.item));
                } else {
                  writer.insert(pageBreak, writer.createPositionBefore(foundPosition.item));
                }
              } catch (error) {
                console.error(`Error inserting page break ${breakIndex + 1}:`, error);
              }
            } else {
              console.warn(`Could not find position for page break ${breakIndex + 1} at HTML position ${htmlPosition}`);
            }
          });
        });
        
        // Flag'leri reset et (setTimeout ile, böylece onChange event'i tamamlanır)
        // Daha uzun bir süre bekle ki tüm onChange event'leri tamamlansın
        setTimeout(() => {
          editor._isSettingData = false;
          editor._isAutoPageBreaking = false;
        }, 500);
      }, 300);
      
      // Page break'lerin görünür olduğundan emin ol
      setTimeout(() => {
        const editableElement = editor.ui.getEditableElement();
        if (editableElement) {
          // CKEditor PageBreak plugin'i farklı selector'lar kullanabilir
          const pageBreaks = editableElement.querySelectorAll(
            '.page-break, [data-page-break], .ck-page-break, [data-ck-page-break], hr[data-page-break]'
          );
          // Model'deki page break'leri de kontrol et
          const model = editor.model;
          const root = model.document.getRoot();
          const range = model.createRangeIn(root);
          const walker = range.getWalker();
          let modelPageBreakCount = 0;
          for (const value of walker) {
            if (value.item.is('element', 'pageBreak')) {
              modelPageBreakCount++;
            }
          }
          
          if (pageBreaks.length === 0 && modelPageBreakCount > 0) {
            console.warn(`Page breaks were added to model (${modelPageBreakCount}) but not found in DOM (${pageBreaks.length}). CKEditor may need time to render them or they may be rendered differently.`);
          }
        }
        // Flag'i burada reset etme, zaten yukarıda reset edildi
      }, 1000);
    } else {
      editor._isAutoPageBreaking = false;
    }
  } catch (error) {
    console.error('Auto page break error:', error);
    console.error('Error stack:', error.stack);
    editor._isAutoPageBreaking = false;
  }
};

/**
 * RichTextEditor - Generic, reusable CKEditor 5 component
 * 
 * Features:
 * - Word-like rich text editing
 * - 50+ font support
 * - Image upload (base64)
 * - Table support
 * - Page break
 * - Mention plugin (optional, for {{key}} insertion)
 * - A4 paper style
 * 
 * Usage:
 * - Template editor: with mentions for question keys
 * - Public demo: readOnly mode
 */
const RichTextEditor = ({
  value = '',
  onChange,
  onReady,
  onBlur,
  onFocus,
  readOnly = false,
  mentionFeeds = null, // Array of { marker, feed, outputText }
  config = {},
  className = ''
}) => {
  // Zoom and view state
  const [zoom, setZoom] = useState(100); // Default 100%
  const [viewMode, setViewMode] = useState('actual-size'); // 'actual-size' - Default: 'actual-size' (100%)
  
  // Resize state
  const [dimensions, setDimensions] = useState({
    width: null, // null = auto (100%)
    height: null // null = auto (calc(100vh - 300px))
  });
  const [isResizing, setIsResizing] = useState(false);

  // Merge default config with custom config
  const editorConfig = useMemo(() => {
    const merged = {
      ...defaultEditorConfig,
      ...config
    };

    // Add mention plugin config if feeds provided
    if (mentionFeeds && mentionFeeds.length > 0) {
      merged.mention = {
        feeds: mentionFeeds
      };
    }

    return merged;
  }, [config, mentionFeeds]);

  // Zoom handlers
  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 15, 250));
    setViewMode('custom');
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 15, 5));
    setViewMode('custom');
  };

  const handleZoomChange = (newValue) => {
    setZoom(newValue);
    setViewMode('custom');
  };

  const handleActualSize = () => {
    setViewMode('actual-size');
    setZoom(100); // Reset zoom
  };

  // Calculate zoom style - CSS variable olarak gönderilecek
  // Zoom sadece sayfa içeriğine (.ck-content) uygulanacak, scroll alanı sabit kalacak
  const getZoomStyle = () => {
    if (viewMode === 'custom') {
      return {
        '--rte-zoom-scale': zoom / 100
      };
    }
    return {
      '--rte-zoom-scale': 1
    };
  };

  // Resize handlers
  const handleResizeStart = (e, handle) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    
    const wrapper = e.currentTarget.closest('.rich-text-editor-wrapper');
    if (!wrapper) return;
    
    const startX = e.clientX;
    const startY = e.clientY;
    const rect = wrapper.getBoundingClientRect();
    const startWidth = dimensions.width !== null ? dimensions.width : rect.width;
    const startHeight = dimensions.height !== null ? dimensions.height : rect.height;

    const handleMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;
      
      let newWidth = startWidth;
      let newHeight = startHeight;
      
      // Handle köşeler ve kenarlar
      if (handle.includes('e')) { // Doğu (sağ)
        newWidth = Math.max(400, startWidth + deltaX);
      }
      if (handle.includes('w')) { // Batı (sol)
        newWidth = Math.max(400, startWidth - deltaX);
      }
      if (handle.includes('s')) { // Güney (alt)
        newHeight = Math.max(300, startHeight + deltaY);
      }
      if (handle.includes('n')) { // Kuzey (üst)
        newHeight = Math.max(300, startHeight - deltaY);
      }
      
      setDimensions({
        width: newWidth,
        height: newHeight
      });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Wrapper style with dimensions
  const getWrapperStyle = () => {
    const style = {};
    if (dimensions.width !== null) {
      style.width = `${dimensions.width}px`;
    }
    if (dimensions.height !== null) {
      style.height = `${dimensions.height}px`;
    }
    return style;
  };

  return (
    <div 
      className={`rich-text-editor-wrapper ${className} view-mode-${viewMode} ${isResizing ? 'is-resizing' : ''}`}
      style={getWrapperStyle()}
    >
      {/* Resize Handles - Köşeler ve Kenarlar */}
      <div 
        className="resize-handle resize-handle-nw" 
        onMouseDown={(e) => handleResizeStart(e, 'nw')}
        title="Köşeden boyutlandır"
      />
      <div 
        className="resize-handle resize-handle-ne" 
        onMouseDown={(e) => handleResizeStart(e, 'ne')}
        title="Köşeden boyutlandır"
      />
      <div 
        className="resize-handle resize-handle-sw" 
        onMouseDown={(e) => handleResizeStart(e, 'sw')}
        title="Köşeden boyutlandır"
      />
      <div 
        className="resize-handle resize-handle-se" 
        onMouseDown={(e) => handleResizeStart(e, 'se')}
        title="Köşeden boyutlandır"
      />
      <div 
        className="resize-handle resize-handle-n" 
        onMouseDown={(e) => handleResizeStart(e, 'n')}
        title="Yukarıdan boyutlandır"
      />
      <div 
        className="resize-handle resize-handle-s" 
        onMouseDown={(e) => handleResizeStart(e, 's')}
        title="Aşağıdan boyutlandır"
      />
      <div 
        className="resize-handle resize-handle-e" 
        onMouseDown={(e) => handleResizeStart(e, 'e')}
        title="Sağdan boyutlandır"
      />
      <div 
        className="resize-handle resize-handle-w" 
        onMouseDown={(e) => handleResizeStart(e, 'w')}
        title="Soldan boyutlandır"
      />
      
      {/* Zoom Control Bar - Reusable Component */}
      <ZoomControlBar
        zoom={zoom}
        onZoomChange={handleZoomChange}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onActualSize={handleActualSize}
        viewMode={viewMode}
        minZoom={5}
        maxZoom={250}
            step={15}
      />

      {/* Editor Container with zoom */}
      <div className="rich-text-editor-content-wrapper" style={getZoomStyle()}>
        <CKEditor
          editor={ClassicEditor}
          data={value}
          config={editorConfig}
          onReady={(editor) => {
            // Toolbar'ın wrapper içinde kalmasını sağla
            const editorElement = editor.ui.getEditableElement();
            if (editorElement) {
              const editorContainer = editorElement.closest('.ck-editor');
              if (editorContainer) {
                // Toolbar'ın wrapper içinde kalmasını garanti et
                editorContainer.style.position = 'relative';
                editorContainer.style.isolation = 'isolate';
              }
              
              // Toolbar elementini bul ve wrapper içinde kalmasını sağla
              const toolbarElement = editorElement.closest('.rich-text-editor-wrapper')?.querySelector('.ck.ck-editor__top');
              if (toolbarElement) {
                toolbarElement.style.position = 'relative';
                toolbarElement.style.zIndex = '100';
              }
            }
            
            // Word'den yapıştırıldığında page break'leri algıla ve düzelt
            const clipboardPipeline = editor.plugins.get('ClipboardPipeline');
            
            // Word'den yapıştırıldığında page break'leri algıla ve düzelt
            // PasteFromOffice'den ÖNCE ham HTML'i kontrol et ve page break'leri koru
            clipboardPipeline.on('inputTransformation', (evt, data) => {
              // Ham HTML'i al
              const htmlString = data.dataTransfer?.getData('text/html');
              if (htmlString) {
                // Word XML formatındaki page break'leri kontrol et
                // Word genelde <w:br w:type="page"/> formatında gönderir
                const wordXmlPageBreak = /<w:br[^>]*w:type=["']page["'][^>]*>/gi;
                const hasWordXmlPageBreak = wordXmlPageBreak.test(htmlString);
                
                // HTML formatındaki page break'leri kontrol et
                const htmlPageBreak = /MsoPageBreak|page-break|mso-page-break|mso-element:\s*page-break/i.test(htmlString);
                
                if (hasWordXmlPageBreak || htmlPageBreak) {
                  // Word XML page break'lerini HTML page break'e çevir
                  let modifiedHtml = htmlString;
                  
                  // Word XML formatı: <w:br w:type="page"/> → <div class="page-break"></div>
                  modifiedHtml = modifiedHtml.replace(
                    /<w:br[^>]*w:type=["']page["'][^>]*>/gi,
                    '<div class="page-break"></div>'
                  );
                  
                  // HTML formatındaki page break'leri de çevir
                  modifiedHtml = modifiedHtml.replace(
                    /<p[^>]*class[^>]*MsoPageBreak[^>]*>.*?<\/p>/gi,
                    '<div class="page-break"></div>'
                  );
                  modifiedHtml = modifiedHtml.replace(
                    /<p[^>]*style[^>]*page-break-before:\s*always[^>]*>.*?<\/p>/gi,
                    '<div class="page-break"></div>'
                  );
                  modifiedHtml = modifiedHtml.replace(
                    /<p[^>]*style[^>]*mso-page-break-before:\s*always[^>]*>.*?<\/p>/gi,
                    '<div class="page-break"></div>'
                  );
                  modifiedHtml = modifiedHtml.replace(
                    /<div[^>]*style[^>]*page-break-before:\s*always[^>]*>/gi,
                    '<div class="page-break"></div>'
                  );
                  
                  // Değiştirilmiş HTML'i geri yükle
                  if (modifiedHtml !== htmlString) {
                    data.dataTransfer.setData('text/html', modifiedHtml);
                  }
                }
              }
            }, { priority: 'high' });
            
            // PasteFromOffice'den SONRA içeriği kontrol et
            // autoPageBreak devre dışı - kullanıcılar manuel Ctrl+Enter ile page break ekleyebilir
            // clipboardPipeline.on('contentInserted', () => {
            //   // İçeriği A4 yüksekliğine göre otomatik sayfalara ayır
            //   setTimeout(async () => {
            //     await autoPageBreak(editor);
            //   }, 1200);
            // });
            
            // Enter tuşuna basıldığında yeni sayfa oluşturma
            // Normal Enter: Yeni paragraf (default davranış)
            // Ctrl+Enter: Yeni sayfa (page break)
            editor.editing.view.document.on('keydown', (evt, data) => {
              if (data.keyCode === 13 && data.ctrlKey) { // Ctrl+Enter = Page Break
                const model = editor.model;
                const selection = model.document.selection;
                const position = selection.getFirstPosition();
                
                model.change((writer) => {
                  // PageBreak eklentisinin model element'ini kullan
                  // CKEditor 5 PageBreak eklentisi 'pageBreak' model element'ini kullanır
                  const pageBreak = writer.createElement('pageBreak');
                  model.insertContent(pageBreak, position);
                  
                  // Page break'ten sonra yeni paragraf ekle
                  const newPosition = writer.createPositionAfter(pageBreak);
                  const paragraph = writer.createElement('paragraph');
                  model.insertContent(paragraph, newPosition);
                  writer.setSelection(newPosition);
                });
                
                data.preventDefault();
                evt.stop();
              }
            });

            if (onReady) {
              onReady(editor);
            }
          }}
          onChange={(event, editor) => {
            const data = editor.getData();
            
            // Eğer setData çağrısı sırasındaysa, ignore et
            if (editor._isSettingData) {
              // onChange callback'ini çağır
              if (onChange) {
                onChange(data);
              }
              return;
            }
            
            // autoPageBreak devre dışı - kullanıcılar manuel Ctrl+Enter ile page break ekleyebilir
            // Önceki timeout'u iptal et
            // if (editor._autoPageBreakTimeout) {
            //   clearTimeout(editor._autoPageBreakTimeout);
            //   editor._autoPageBreakTimeout = null;
            // }
            
            // İçerik değiştiğinde otomatik sayfa oluştur (devre dışı)
            // editor._autoPageBreakTimeout = setTimeout(async () => {
            //   if (!editor._isSettingData && !editor._isAutoPageBreaking) {
            //     await autoPageBreak(editor);
            //   }
            //   editor._autoPageBreakTimeout = null;
            // }, 800);
            
            if (onChange) {
              onChange(data);
            }
          }}
          onBlur={onBlur}
          onFocus={onFocus}
          disabled={readOnly}
        />
      </div>
    </div>
  );
};

RichTextEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  onReady: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  readOnly: PropTypes.bool,
  mentionFeeds: PropTypes.arrayOf(
    PropTypes.shape({
      marker: PropTypes.string.isRequired,
      feed: PropTypes.func.isRequired,
      outputText: PropTypes.func.isRequired
    })
  ),
  config: PropTypes.object,
  className: PropTypes.string
};

RichTextEditor.defaultProps = {
  value: '',
  readOnly: false,
  mentionFeeds: null,
  config: {},
  className: ''
};

export default RichTextEditor;

